import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import TextField from "@mui/material/TextField";

import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_board_card_comments,
  API_post_board_card_comment,
} from "../../../constants";

// Mirrors the MUI input styling used across the app's modals.
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
  },
};

const BoardCardComments = ({ storeId, cardUuid, onCommentPosted }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRightworkAPI(API_get_board_card_comments, {
        store_id: storeId,
        board_card_uuid: cardUuid,
      });
      // Backend returns oldest-first; show newest-first so a freshly posted
      // comment lands right under the input.
      const fetched = res.data?.redux?.payload?.board_card_comments || [];
      setComments(fetched.slice().reverse());
    } catch (error) {
      toast.error("Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [storeId, cardUuid]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const submit = async () => {
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const res = await postRightworkAPI(API_post_board_card_comment, {
        store_id: storeId,
        board_card_uuid: cardUuid,
        text,
      });
      const comment = res.data?.redux?.payload?.board_card_comment;
      if (comment) {
        setComments((prev) => [comment, ...prev]);
        setDraft("");
        if (onCommentPosted) onCommentPosted();
      }
    } catch (error) {
      toast.error("Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 mb-1.5">COMMENTS</p>

      <TextField
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Write a comment…"
        fullWidth
        variant="outlined"
        size="small"
        multiline
        minRows={2}
        sx={textFieldSx}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
      />
      <button
        className="mt-1.5 px-3 py-1.5 rounded-lg bg-violet-700 hover:bg-violet-800 disabled:bg-slate-300 text-white text-sm font-medium"
        onClick={submit}
        disabled={posting || !draft.trim()}
      >
        Comment
      </button>

      <div className="mt-3 space-y-3">
        {loading ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : comments.length === 0 ? (
          <p className="text-xs text-slate-400">No comments yet.</p>
        ) : (
          comments.map((c) => (
            <div key={c.uuid} className="flex gap-2">
              <div className="h-7 w-7 shrink-0 rounded-full bg-violet-100 flex items-center justify-center">
                <span className="text-[11px] font-semibold text-violet-700">
                  {c.author?.initials}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-slate-800">
                    {c.author?.full_name}
                  </span>
                  <span className="text-xs text-slate-400">
                    {c.created_at_formatted}
                  </span>
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                  {c.text}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BoardCardComments;
